import { formatDateMMDDYYYY, formatDateTimeMMDDYYYY } from '../../../../utils/formatUtils';
import type {
    StoreDetailsCreateNotificationPayload,
    StoreDetailsNotification,
    StoreDetailsNotificationRecipientResponse,
    StoreDetailsNotificationResponse,
    StoreDetailsSendNotificationFormValues,
} from '../../../../types/features/storeDetails/notifications';

const buildNotificationRowId = (
    notification: StoreDetailsNotificationResponse,
    recipient?: StoreDetailsNotificationRecipientResponse,
): string => `${notification.id}-${recipient?.id || 'notification'}`;

const getRecipientStatusTone = (status?: string | null): StoreDetailsNotification['statusTone'] => {
    switch ((status || '').toLowerCase()) {
        case 'sent':
            return 'success';
        case 'failed':
            return 'danger';
        case 'pending':
            return 'warning';
        default:
            return 'neutral';
    }
};

const formatRecipientStatusLabel = (status?: string | null): string => {
    const normalizedStatus = (status || '').trim().toLowerCase();
    if (!normalizedStatus) return '-';

    return normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
};

export const getStoreDetailsNotificationStatuses = (
    notifications: StoreDetailsNotificationResponse[],
): string[] => Array.from(new Set(
    notifications.flatMap((notification) => [
        formatRecipientStatusLabel(notification.status),
        ...(Array.isArray(notification.recipients)
            ? notification.recipients.map((recipient) => formatRecipientStatusLabel(recipient.status))
            : []),
    ]).filter((status) => status !== '-'),
)).sort();

export const mapStoreDetailsNotificationRows = (
    notifications: StoreDetailsNotificationResponse[],
): StoreDetailsNotification[] => (
    notifications.flatMap((notification) => {
        const recipients = Array.isArray(notification.recipients) ? notification.recipients : [];
        const baseRow = {
            reportDate: formatDateMMDDYYYY(notification.report_date, '-'),
            title: notification.title || '-',
            message: notification.message || '-',
        };

        if (recipients.length === 0) {
            return [{
                ...baseRow,
                id: buildNotificationRowId(notification),
                user: '-',
                status: '-',
                statusTone: 'neutral' as const,
                viewedAt: '-',
            }];
        }

        return recipients.map((recipient) => ({
            ...baseRow,
            id: buildNotificationRowId(notification, recipient),
            user: recipient.user_name || recipient.store_user_id || '-',
            status: formatRecipientStatusLabel(recipient.status),
            statusTone: getRecipientStatusTone(recipient.status),
            viewedAt: recipient.viewed_at ? formatDateTimeMMDDYYYY(recipient.viewed_at, { fallback: '-' }) : '-',
        }));
    })
);

const formatTodayForNotificationReportDate = (): string => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = String(today.getFullYear());

    return `${year}-${month}-${day}`;
};

export const buildStoreDetailsCreateNotificationPayload = (
    storeId: string,
    values: StoreDetailsSendNotificationFormValues,
): StoreDetailsCreateNotificationPayload => {
    const auditId = values.auditId.trim();

    return {
        store_id: storeId,
        notification_type: values.critical ? 'critical' : 'warning',
        ...(auditId ? { audit_id: auditId } : {}),
        title: values.title.trim(),
        message: values.message.trim(),
        status: 'ready',
        report_date: formatTodayForNotificationReportDate(),
        recipients: values.userIds.map((userId) => ({
            user_id: userId,
            channel: values.channel,
        })),
    };
};
